/**
 * Year Detail Sheet — full year editor + stats viewer.
 * 1) Stage picker — choose life period for this year
 * 2) Events editor — add/edit up to 3 custom events (emoji + name)
 * 3) Auto stats — habits, challenges, goals, pomodoro from DB
 * 4) Famous milestones at this age
 */

import { memo, useMemo, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Target, Flame, Trophy, Calendar, CheckCircle, Star,
  Timer, Plus, X,
} from 'lucide-react'
import { BottomSheet } from '@turnifly/shared/ui'
import { useYearStats } from '@turnifly/shared/hooks'
import {
  useLifeAnnotationsStore,
  STAGE_PRESETS,
  getPresetById,
} from '@turnifly/shared/stores'
import type { FamousMilestone, TimelineEvent } from '@turnifly/shared/api'

// ── Common emoji list for event picker ──
const EVENT_EMOJIS = [
  '🏠', '🚗', '💍', '👶', '🎓', '💼', '🏆', '✈️', '📱', '💰',
  '🏢', '📚', '🎯', '⭐', '❤️', '🔑', '🎵', '🎨', '🏅', '🌟',
  '🚀', '💎', '🎉', '🏥', '🌍', '📝', '🤝', '🏋️', '🧠', '🙏',
]

interface YearDetailSheetProps {
  isOpen: boolean
  onClose: () => void
  yearOfLife: number
  calendarYear: number
  isLived: boolean
  isCurrent: boolean
  famousMilestones: FamousMilestone[]
  userAchievements: TimelineEvent[]
}

export const YearDetailSheet = memo(function YearDetailSheet({
  isOpen,
  onClose,
  yearOfLife,
  calendarYear,
  isLived,
  isCurrent,
  famousMilestones,
  userAchievements,
}: YearDetailSheetProps) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  const { annotations, setYearStage, addYearEvent, removeYearEvent } = useLifeAnnotationsStore()
  const annotation = annotations[calendarYear] ?? { stageId: null, events: [] }
  const currentStage = annotation.stageId ? getPresetById(annotation.stageId) : null

  const { data: stats, isLoading } = useYearStats(calendarYear)

  const [showEventForm, setShowEventForm] = useState(false)
  const [newEmoji, setNewEmoji] = useState('⭐')
  const [newName, setNewName] = useState('')

  const yearEvents = useMemo(
    () => userAchievements.filter((a) => new Date(a.date).getFullYear() === calendarYear),
    [userAchievements, calendarYear],
  )
  const ageMilestones = useMemo(
    () => famousMilestones.filter((m) => m.age === yearOfLife),
    [famousMilestones, yearOfLife],
  )

  const handleAddEvent = useCallback(() => {
    if (!newName.trim()) return
    addYearEvent(calendarYear, { emoji: newEmoji, name: newName.trim() })
    setNewName('')
    setNewEmoji('⭐')
    setShowEventForm(false)
  }, [calendarYear, newEmoji, newName, addYearEvent])

  const handleStageSelect = useCallback((id: string) => {
    setYearStage(calendarYear, annotation.stageId === id ? null : id)
  }, [calendarYear, annotation.stageId, setYearStage])

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={`${calendarYear}`}>
      <div className="px-5 pb-6 space-y-5">

        {/* ═══ 1. AGE BADGE ═══ */}
        <div className="flex items-center gap-2">
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{
              backgroundColor: (currentStage?.color ?? '#64748B') + '18',
              border: `1px solid ${currentStage?.color ?? '#64748B'}30`,
            }}
          >
            <span className="text-sm">{currentStage?.icon ?? '📅'}</span>
            <span className="text-xs font-semibold" style={{ color: currentStage?.color ?? '#64748B' }}>
              {yearOfLife} {t('life.yearsOld')}
            </span>
          </div>
          {currentStage && (
            <span className="text-[10px] text-hint">
              {t(`life.stages.${currentStage.id}`)}
            </span>
          )}
        </div>

        {/* ═══ 2. STAGE PICKER ═══ */}
        <div>
          <h4 className="text-[10px] font-semibold text-hint uppercase tracking-wider mb-2">
            {t('life.pickStage')}
          </h4>
          <div className="grid grid-cols-5 gap-1.5">
            {STAGE_PRESETS.map((preset) => {
              const active = annotation.stageId === preset.id
              return (
                <motion.button
                  key={preset.id}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleStageSelect(preset.id)}
                  className={`flex flex-col items-center gap-0.5 py-2 rounded-xl tap-highlight-transparent transition-all ${
                    active
                      ? 'border-2'
                      : 'bg-white/[0.03] border border-white/[0.06]'
                  }`}
                  style={active ? {
                    backgroundColor: preset.color + '15',
                    borderColor: preset.color + '50',
                  } : undefined}
                >
                  <span className="text-base">{preset.icon}</span>
                  <span className={`text-[7px] font-medium leading-tight ${active ? '' : 'text-hint'}`}
                    style={active ? { color: preset.color } : undefined}
                  >
                    {t(`life.stages.${preset.id}`)}
                  </span>
                </motion.button>
              )
            })}
          </div>
        </div>

        {/* ═══ 3. CUSTOM EVENTS ═══ */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] font-semibold text-hint uppercase tracking-wider">
              {t('life.bigEvents')} ({annotation.events.length}/3)
            </h4>
            {annotation.events.length < 3 && (
              <button
                onClick={() => setShowEventForm(true)}
                className="flex items-center gap-1 text-[10px] font-medium text-primary tap-highlight-transparent"
              >
                <Plus className="w-3 h-3" />
                {t('life.addEvent')}
              </button>
            )}
          </div>

          {/* Existing events */}
          <div className="space-y-1.5">
            {annotation.events.map((ev, i) => (
              <div
                key={`${ev.emoji}-${i}`}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]"
              >
                <span className="text-lg">{ev.emoji}</span>
                <span className="text-xs text-text flex-1">{ev.name}</span>
                <button
                  onClick={() => removeYearEvent(calendarYear, i)}
                  className="p-1 rounded-full hover:bg-white/[0.06] tap-highlight-transparent"
                >
                  <X className="w-3 h-3 text-hint" />
                </button>
              </div>
            ))}
          </div>

          {/* Add event form */}
          <AnimatePresence>
            {showEventForm && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] space-y-3">
                  {/* Emoji grid */}
                  <div className="grid grid-cols-10 gap-1">
                    {EVENT_EMOJIS.map((e) => (
                      <button
                        key={e}
                        onClick={() => setNewEmoji(e)}
                        className={`flex items-center justify-center h-8 rounded-lg tap-highlight-transparent text-base ${
                          newEmoji === e ? 'bg-primary/20 ring-1 ring-primary/40' : 'hover:bg-white/[0.04]'
                        }`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                  {/* Name input */}
                  <div className="flex gap-2">
                    <div className="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08]">
                      <span className="text-base">{newEmoji}</span>
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder={t('life.eventNamePlaceholder')}
                        maxLength={40}
                        className="flex-1 bg-transparent text-xs text-text placeholder:text-hint outline-none"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddEvent()}
                      />
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={handleAddEvent}
                      disabled={!newName.trim()}
                      className="px-4 py-2 rounded-lg bg-primary text-white text-xs font-semibold disabled:opacity-40 tap-highlight-transparent"
                    >
                      {t('common.add')}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ═══ 4. AUTO STATS (from DB) ═══ */}
        {(isLived || isCurrent) && (
          <div>
            <h4 className="text-[10px] font-semibold text-hint uppercase tracking-wider mb-2">
              {t('life.yearStats.title')}
            </h4>
            <div className="grid grid-cols-3 gap-1.5">
              <StatChip
                icon={<Calendar className="w-3 h-3 text-primary" />}
                label={t('life.yearStats.habits')}
                value={isLoading ? '…' : String(stats?.habitsActive ?? 0)}
              />
              <StatChip
                icon={<CheckCircle className="w-3 h-3 text-success" />}
                label={t('life.yearStats.completionDays')}
                value={isLoading ? '…' : String(stats?.habitCompletionDays ?? 0)}
              />
              <StatChip
                icon={<Target className="w-3 h-3 text-accent" />}
                label={t('life.yearStats.goalsAchieved')}
                value={isLoading ? '…' : String(stats?.goalsAchieved ?? 0)}
              />
              <StatChip
                icon={<Trophy className="w-3 h-3 text-warning" />}
                label={t('life.yearStats.challenges')}
                value={isLoading ? '…' : String(stats?.challengesCompleted ?? 0)}
              />
              <StatChip
                icon={<Timer className="w-3 h-3 text-danger" />}
                label={t('life.yearStats.focus')}
                value={isLoading ? '…' : formatFocusTime(stats?.focusMinutes ?? 0)}
              />
              <StatChip
                icon={<Flame className="w-3 h-3 text-streak" />}
                label={t('life.yearStats.streak')}
                value={isLoading ? '…' : `${stats?.longestStreak ?? 0} ${t('life.days')}`}
              />
            </div>
          </div>
        )}

        {/* ═══ 5. TURNIFLY ACHIEVEMENTS ═══ */}
        {yearEvents.length > 0 && (
          <div>
            <h4 className="text-[10px] font-semibold text-hint uppercase tracking-wider mb-2">
              {t('life.turnifly')}
            </h4>
            <div className="space-y-1">
              {yearEvents.map((ev) => (
                <div
                  key={ev.date + ev.label}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.02]"
                >
                  <span className="text-sm">{ev.icon}</span>
                  <span className="text-[11px] text-text flex-1 truncate">{ev.label}</span>
                  <span className="text-[9px] text-hint">{ev.date.slice(5, 10)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ 6. FAMOUS MILESTONES ═══ */}
        {ageMilestones.length > 0 && (
          <div>
            <h4 className="text-[10px] font-semibold text-hint uppercase tracking-wider mb-2">
              {t('life.famousPeople')}
            </h4>
            <div className="space-y-1">
              {ageMilestones.map((m) => (
                <div key={m.id} className="flex items-start gap-2 px-3 py-1.5 rounded-lg bg-primary/[0.03]">
                  <Star className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <span className="text-[10px] font-bold text-primary">{m.person}</span>
                    <p className="text-[10px] text-hint truncate">
                      {lang === 'uz' ? m.achievement_uz : m.achievement_en}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty future */}
        {!isLived && !isCurrent && yearEvents.length === 0 && ageMilestones.length === 0 && annotation.events.length === 0 && (
          <div className="text-center py-4">
            <span className="text-2xl">🌱</span>
            <p className="text-xs text-hint mt-2">{t('life.futureAwaits')}</p>
          </div>
        )}
      </div>
    </BottomSheet>
  )
})

// ── Focus time formatter ──

function formatFocusTime(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

// ── Stat chip ──

function StatChip({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-white/[0.03] border border-white/[0.04]">
      {icon}
      <div className="min-w-0">
        <span className="text-[7px] text-hint block leading-tight">{label}</span>
        <span className="text-xs font-display font-bold text-text tabular-nums">{value}</span>
      </div>
    </div>
  )
}
